import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import NumberHelper from 'util/number_helper';
import FilterSet from 'components/Explorer/models/filter_set';
import * as QueryFetcher from 'components/Explorer/models/query_fetcher';
import Editor from './Editor';
import useQueryParams from 'hooks/useQueryParams';

const JournalList = ({ filterSet, onChange, analyticsDialogueName }) => {
  const params = useQueryParams();

  const dialogRef = useRef(null);
  const newButtonRef = useRef(null);
  const editButtonRef = useRef(null);

  const journalListId = filterSet.get('journal_list_id');

  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(Boolean(journalListId));
  const [journalList, setJournalList] = useState(null);

  useEffect(() => {
    if (!journalListId) {
      setJournalList(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const request = QueryFetcher.fetchJSON('filters/journal_list', params);

    request.then((err, response) => {
      if (err?.name === 'AbortError') return;

      if (!err && response.body) {
        setJournalList(response.body);
      }
      setLoading(false);
    });

    return () => request.abort();
  }, []);

  // Defer to the next microtask so React has time to render the new buttons
  // before we move focus — refocus is called immediately after setJournalList.
  const refocus = (next) => {
    queueMicrotask(() => {
      if (next) {
        editButtonRef.current?.focus();
      } else {
        newButtonRef.current?.focus();
      }
    });
  };

  const openEditor = () => {
    setShowEditor(true);
    dialogRef.current?.showModal();
  };

  const closeEditor = () => {
    setShowEditor(false);
    dialogRef.current?.close();
  };

  const saveEditor = (next) => {
    filterSet.set('journal_list_id', next.id);
    filterSet.clear('journal_id[]');
    closeEditor();
    setJournalList(next);
    if (onChange) onChange();
    refocus(next);
  };

  const clearEditor = () => {
    filterSet.clear('journal_list_id');
    closeEditor();
    setJournalList(null);
    if (onChange) onChange();
    refocus(null);

    Analytics.trackEvent('Advanced Search: Modal Dialogue Cleared', {
      dialogue_name: analyticsDialogueName
    });
  };

  const cancelEditor = () => {
    closeEditor();
    refocus(journalList);
  };

  const editor = (
    <dialog ref={dialogRef} onKeyDown={(event) => event.stopPropagation()}>
      {showEditor && (
        <Editor
          journalList={journalList}
          onCancel={cancelEditor}
          onSave={saveEditor}
          analyticsDialogueName={analyticsDialogueName}
        />
      )}
    </dialog>
  );

  if (loading) {
    return <div>{I18n.t('Explorer.AdvancedSearch.fields.journal.loading')}</div>;
  }

  if (journalListId) {
    return (
      <div className="Explorer-AdvancedSearch-JournalListDescription">
        <div className="description">
          {I18n.t('Explorer.AdvancedSearch.fields.journal.editor.issn_count', {
            count: journalList.count,
            formattedCount: NumberHelper.formatNumberWithDelimiter(journalList.count)
          })}
        </div>

        <div className="edit-buttons">
          <button type="button" className="edit" onClick={openEditor} ref={editButtonRef}>
            {I18n.t('Explorer.AdvancedSearch.fields.journal.editor.edit')}
          </button>

          <button type="button" className="clear" onClick={clearEditor}>
            {I18n.t('Explorer.AdvancedSearch.fields.journal.editor.clear')}
          </button>
        </div>
        {editor}
      </div>
    );
  }

  return (
    <div>
      <div className="JournalFilter-journalList-content">
        <div className="JournalFilter-journalList-create-buttons">
          or &nbsp;
          <button
            type="button"
            className="JournalFilter-journalList-add-button"
            onClick={openEditor}
            ref={newButtonRef}
          >
            {I18n.t('Explorer.AdvancedSearch.fields.journal.editor.open')}
          </button>
        </div>
      </div>
      {editor}
    </div>
  );
};

JournalList.propTypes = {
  filterSet: PropTypes.instanceOf(FilterSet).isRequired,
  onChange: PropTypes.func,
  analyticsDialogueName: PropTypes.string
};

export default JournalList;
